/**
 * Pure builder for the Match Hub "Team Comparison" card.
 * Produces head-to-head stat rows (home value · label · away value) from each team's
 * recent record + curated facts, emitting a row only when at least one side has a value.
 */

import type { MatchHubRecentRecord, MatchHubTeamFacts } from '../types/matchHub.types';

const DASH = '—';

export interface TeamComparisonSide {
  record?: MatchHubRecentRecord;
  facts?: MatchHubTeamFacts | null;
}

export interface TeamComparisonRow {
  label: string;
  home: string;
  away: string;
}

function recentRecordDisplay(record: MatchHubRecentRecord | undefined): string | null {
  if (!record || record.played <= 0) return null;
  return `${record.won}W ${record.drawn}D ${record.lost}L · ${record.goalsFor}–${record.goalsAgainst}`;
}

const ROW_DEFS: ReadonlyArray<{ label: string; get: (side: TeamComparisonSide) => string | null }> = [
  { label: 'FIFA rank', get: (s) => (typeof s.facts?.fifaRanking === 'number' ? `#${s.facts.fifaRanking}` : null) },
  { label: 'Squad value', get: (s) => s.facts?.squadValue ?? null },
  { label: 'Key player', get: (s) => s.facts?.keyPlayer ?? null },
  { label: 'Last 5', get: (s) => recentRecordDisplay(s.record) },
  { label: 'WC titles', get: (s) => (typeof s.facts?.wcTitles === 'number' ? String(s.facts.wcTitles) : null) },
  { label: 'Best WC', get: (s) => s.facts?.wcBestFinish ?? null },
  { label: 'Appearances', get: (s) => (typeof s.facts?.wcAppearances === 'number' ? String(s.facts.wcAppearances) : null) },
];

export function buildTeamComparisonRows(
  home: TeamComparisonSide,
  away: TeamComparisonSide,
): TeamComparisonRow[] {
  const rows: TeamComparisonRow[] = [];
  for (const def of ROW_DEFS) {
    const h = def.get(home);
    const a = def.get(away);
    if (h === null && a === null) continue;
    rows.push({ label: def.label, home: h ?? DASH, away: a ?? DASH });
  }
  return rows;
}
